/* eslint-disable react-hooks/exhaustive-deps */
import { RoleFunction } from '@eagle/common';
import { Account, AccountRequest, AccountTemplate, AccountType, StakeholderAccountResult } from '@eagle/core-data-types';
import { Alert, Button } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../../auth';
import { LOOKUP_DEBOUNCE_TIME } from '../../../constants';
import { evaluate } from '../../../evaluator';
import { useFetchAllCache, usePromise, useSwitchAwareConfig, useUiTemplate } from '../../../hooks';
import { CacheDataTypes, Undefinable } from '../../../types';
import { LIFECYCLE_STAGE_UPDATE_LIST_TEMPLATE } from '../../../ui-templates';
import { filterDeletedCache, testid, useHasAuthorization } from '../../../util';
import { useBoolFlag } from '../../flags';
import { Lookup } from '../../lookup';
import { MiddleSpinner } from '../../middle-spinner';
import { SelectionCheckBox } from '../../selection-check-box';
import { StakeHolderAccountCard } from '../../stakeholder-card';
import { useStageConfirmDialogContext } from './stage-confirm-dialog-context';
import { StageConfirmDialogCreateAccount } from './stage-confirm-dialog-create-account';
import { StageConfirmDialogEditAccountRequest } from './stage-confirm-dialog-edit-account-request';
import { StakeholderState } from './stage-confirm-dialog-reducer';
import { StakeholderCardBase } from './stakeholder-card-base';

const ACCOUNT_CREATOR = [RoleFunction.ACCOUNT_CREATOR] as const;
const ACCOUNT_REQUESTER = [RoleFunction.ACCOUNT_REQUESTER] as const;

interface EditableLookupProps extends PropsWithChildren {
  accountRequest: Undefinable<AccountRequest>;
  accountTemplates: AccountTemplate[];
  invalidateAccount: VoidFunction;
  isAccountCreator: boolean;
  isAccountRequester: boolean;
  selectedAccount: Undefinable<StakeholderAccountResult>;
  stakeholderState: StakeholderState;
}

interface AccountSelectListItemProps {
  account: Account;
  query: string;
}

const CREATE_NEW_ACCOUNT = 'CREATE_NEW_ACCOUNT';

const EditableLookup: FC<EditableLookupProps> = ({
  accountRequest,
  accountTemplates,
  invalidateAccount,
  isAccountCreator,
  isAccountRequester,
  selectedAccount,
  stakeholderState,
}) => {
  const { dispatch, sharedThingId, state, stage } = useStageConfirmDialogContext();
  const { t } = useTranslation(['admin', 'common']);
  const role = stakeholderState.role;
  const { account, accountType: loggedInUserAccountType, restClient } = useAuthenticated();
  const theme = useTheme();
  const { productName } = useSwitchAwareConfig();

  const { template: selectListTemplate, loaded: isTemplateLoaded } = useUiTemplate('lifecycle-stage-update-list', LIFECYCLE_STAGE_UPDATE_LIST_TEMPLATE);

  const canCreateNewAccount = !state.isSimplifiedAccountSelect && accountTemplates.length > 0 && (isAccountCreator || (isAccountRequester && stakeholderState.allowAccountRequests));

  const hasInitialAccount = Boolean(stakeholderState.initialAccountId);
  const isSearchDisabled = !stakeholderState.canEdit || stakeholderState.isRemoveConfirmationChecked;

  const canEditAccountRequest = (isAccountCreator || isAccountRequester) && accountRequest && accountRequest.productName === productName;

  const handleFindAccounts = useMemo<(_: string) => Promise<(Account | typeof CREATE_NEW_ACCOUNT)[]>>(() => {
    const additionalParams = stakeholderState.allowAccountRequests ? { includeAccountRequests: true } : {};

    return async (text: string) => restClient.sharedThing.getStakeholderAccounts(sharedThingId, stakeholderState.role,
      {
        search: text,
        ...additionalParams,
      },
    ).then((accounts) => {
      if (!accounts) {
        return [];
      }
      if (canCreateNewAccount) {
        return [...accounts, CREATE_NEW_ACCOUNT];
      }
      return accounts;
    });
  }, [restClient, sharedThingId, stakeholderState.role, stakeholderState.allowAccountRequests, canCreateNewAccount]);

  const [accountType, , accountTypeState] = usePromise(async () => {
    if (!stakeholderState.accountId) return;
    return restClient.sharedThing.getStakeholderAccountType(sharedThingId, role, stakeholderState.accountId);
  }, [restClient, sharedThingId, stakeholderState.role, stakeholderState.accountId]);

  const AccountSelectListItem: FC<AccountSelectListItemProps> = ({ account, query }) => {
    if (!isTemplateLoaded) {
      return null;
    }
    return <>{evaluate(selectListTemplate, { account, query })}</>;
  };

  const accountLookup = (
    <>
      <Lookup<Account | typeof CREATE_NEW_ACCOUNT>
        data-testid={testid`${stakeholderState.role}-account-lookup`}
        debounceTime={LOOKUP_DEBOUNCE_TIME}
        disabled={state.config?.forceEditableStakeholderSelect ? false : isSearchDisabled}
        disablePortal={false}
        loadOnRender
        disableClearable={Boolean(state.removeConfirmation)}
        getOptionTestId={(option) => {
          if (option === CREATE_NEW_ACCOUNT) {
            return testid`${stakeholderState.role}-create-account-button`;
          }
          return testid`${stakeholderState.role}-account-item-${option._id}`;
        }}
        optionStyle={{ padding: 0 }}
        handleCompareItems={(option, value) => option === CREATE_NEW_ACCOUNT || value === CREATE_NEW_ACCOUNT ? false : option._id === value._id}
        handleFindItems={handleFindAccounts}
        handleFormatListItem={(item) => {
          if (item === CREATE_NEW_ACCOUNT) {
            return (
              <Box sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                {t('admin:page.thing-detail.update-stage-dialog.action.create-account')}
              </Box>
            );
          }

          return (
            <AccountSelectListItem
              account={item}
              query={stakeholderState.lastAccountSearch ?? ''}
            />
          );
        }}
        handleFormatSelectedItem={(item) => item === CREATE_NEW_ACCOUNT ? '' : item.display}
        isSearch
        onInputChange={(_event, inputValue, reason) => {
          if (reason === 'input') {
            dispatch({ type: 'SET_LAST_ACCOUNT_SEARCH', role: stakeholderState.role, search: inputValue });
          }
        }}
        onItemSelected={(item) => {
          if (item === CREATE_NEW_ACCOUNT) {
            dispatch({ type: 'START_STAKEHOLDER_CREATE_MODE', role: stakeholderState.role, accountTemplates, account });
          }
          else {
            dispatch({ type: 'SET_ACCOUNT', role: stakeholderState.role, account: item, accountId: item?._id ?? null });
          }
        }}
        placeholder={stakeholderState.config?.selectPlaceholder}
        required={stakeholderState.isRequired}
        selectedItem={selectedAccount ?? stakeholderState.account ?? null}
        size="small"
      />
      {state.removeConfirmation && hasInitialAccount && !stage.enterConditions?.requiredStakeholders?.includes(stakeholderState.role) && (
        <SelectionCheckBox
          checked={stakeholderState.isRemoveConfirmationChecked}
          data-testid={testid`${stakeholderState.role}-confirm-remove-checkbox`}
          handleClick={(e) => {
            dispatch({ type: 'SET_REMOVE_CONFIRMATION', role: stakeholderState.role, value: e.target.checked });
          }}
          label={state.removeConfirmation.checkboxLabel}
          sx={{ pl: 0 }}
        />
      )}
    </>
  );

  return (
    <StakeholderCardBase
      hideRole={state.isSimplifiedAccountSelect}
      role={role}
      action={state.isSimplifiedAccountSelect ? null : (hasInitialAccount ? !isSearchDisabled : Boolean(selectedAccount)) && (
        stakeholderState.isEditing ? (
          <Button
            data-testid={testid`${stakeholderState.role}-cancel-button`}
            onClick={() => {
              dispatch({ type: 'CANCEL_STAKEHOLDER_EDIT_MODE', role });
            }}
            size="small"
          >
            {t('common:common.action.cancel')}
          </Button>
        ) : (
          <Button
            data-testid={testid`${stakeholderState.role}-edit-button`}
            onClick={() => {
              dispatch({ type: 'START_STAKEHOLDER_EDIT_MODE', role });
            }}
            size="small"
          >
            {t('admin:page.thing-detail-update-stage-dialog.action.edit')}
          </Button>
        )
      )}>

      {state.isSimplifiedAccountSelect ? (
        <>
          {(stakeholderState.accountId && !selectedAccount && !stakeholderState.account) ? <MiddleSpinner /> : accountLookup}
        </>
      ) : (
        <>
          {stakeholderState.isEditing || (!hasInitialAccount && !stakeholderState.accountId) ? accountLookup : null}
          {(stakeholderState.accountId && (!selectedAccount || accountTypeState === 'pending')) && <MiddleSpinner />}
        </>
      )}

      {loggedInUserAccountType?.display === 'Manufacturer' && selectedAccount?.isAccountRequest && !stakeholderState.isEditing && (
        <Alert severity="warning">
          {t('admin:page.thing-detail-update-stage-dialog.account-request.hint')}
        </Alert>
      )}
      {selectedAccount && accountTypeState !== 'pending' && (
        (stakeholderState.isEditing && selectedAccount.isAccountRequest && canEditAccountRequest) ? (
          <StageConfirmDialogEditAccountRequest
            accountRequest={accountRequest}
            invalidateAccount={invalidateAccount}
            stakeholderState={stakeholderState}
          />
        ) : state.isSimplifiedAccountSelect ? null : (
          <StakeHolderAccountCard
            accountType={accountType}
            display={selectedAccount.display}
            properties={selectedAccount.properties}
            role={role}
            initialUsers={accountRequest?.targetAccountInfo.initialUsers ?? null}
          />
        )
      )}
    </StakeholderCardBase >
  );
};

interface StageConfirmDialogCardProps {
  stakeholderState: StakeholderState;
  onValidationChange: (role: string, isValid: boolean) => void;
}

export const StageConfirmDialogCard: FC<StageConfirmDialogCardProps> = ({
  stakeholderState,
  onValidationChange,
}) => {
  const { hasAuthorization } = useHasAuthorization();
  const accountTemplatesCache = useFetchAllCache(CacheDataTypes.ACCOUNT_TEMPLATE);
  const accountTypesCache = useFetchAllCache(CacheDataTypes.ACCOUNT_TYPE);
  const isUsersMandatory = useBoolFlag('admin-life-cycle-users-are-mandatory-feature');

  const { restClient } = useAuthenticated();
  const { sharedThingId } = useStageConfirmDialogContext();
  const [invalidate, setInvalidate] = useState(new Date());

  const accountCreator = hasAuthorization(ACCOUNT_CREATOR);
  const accountRequester = hasAuthorization(ACCOUNT_REQUESTER);
  const isAccountCreatorOrRequester = accountCreator || accountRequester;

  const [accountResponse] = usePromise(async () => {
    if (!stakeholderState.accountId) {
      return undefined;
    }

    const account = await restClient.sharedThing.getStakeholderAccount(sharedThingId, stakeholderState.role, stakeholderState.accountId);
    let accountRequest: Undefinable<AccountRequest>;
    if (account.isAccountRequest) {
      accountRequest = await restClient.accountRequest.get(stakeholderState.accountId);
    }

    return {
      account,
      accountRequest,
    };
  }, [stakeholderState.accountId, stakeholderState.role, sharedThingId, invalidate, restClient]);

  useEffect(() => {
    if (!accountResponse?.accountRequest) {
      onValidationChange(stakeholderState.role, true);
      return;
    }
    const hasUsers = Boolean(accountResponse.accountRequest.targetAccountInfo.initialUsers?.length);
    onValidationChange(stakeholderState.role, hasUsers || !isUsersMandatory);
  }, [accountResponse, isUsersMandatory, onValidationChange, stakeholderState.role]);

  const [accountTemplates] = usePromise(async () => {
    if (!isAccountCreatorOrRequester) {
      return [];
    }
    return filterDeletedCache<AccountTemplate>(accountTemplatesCache);
  }, [accountTemplatesCache, isAccountCreatorOrRequester]);

  const [accountTypes] = usePromise(async () => {
    if (!isAccountCreatorOrRequester) {
      return [];
    }
    return filterDeletedCache<AccountType>(accountTypesCache);
  }, [accountTypesCache, isAccountCreatorOrRequester]);

  if (!accountTypes || !accountTemplates) {
    return <MiddleSpinner />;
  }

  const availableAccountTemplates = accountTemplates.filter((accountTemplate) => stakeholderState.accountTemplateIds.includes(accountTemplate._id)) ?? [];
  if (stakeholderState.isCreatingNewAccount) {
    return (
      <StageConfirmDialogCreateAccount
        accountTypes={accountTypes}
        accountTemplates={availableAccountTemplates}
        stakeholderState={stakeholderState}
        onValidation={onValidationChange}
      />
    );
  }

  return (
    <EditableLookup
      accountRequest={accountResponse?.accountRequest}
      accountTemplates={availableAccountTemplates}
      invalidateAccount={() => { setInvalidate(new Date()); }}
      isAccountCreator={accountCreator}
      isAccountRequester={accountRequester}
      stakeholderState={stakeholderState}
      selectedAccount={accountResponse?.account}
    />
  );
};

